import { ManShiftData } from '@tymbe/schema/man-shift.interface';
import { PersonData } from '@tymbe/schema/person.interface';
import { ShiftData } from '@tymbe/schema/shift.interface';
import moment from 'moment';

export interface ManShiftRecord {
  time: moment.Moment | string;
  operation: string;
  data: {
    manShiftId: number;
    person: PersonData | undefined;
  };
}

const enum TimelineOperation {
  CREATE = 'create',
  DELETE = 'delete',
}

const createTimelineEntry = (record: ManShiftRecord) => ({
  time: moment(record.time),
  operation: record.operation,
  data: record.data,
});

export const processShiftTimeline = (shift: ShiftData | undefined) => {
  const manshiftUpdatesByUser: Record<number, ManShiftRecord[]> = {};

  shift?.manShift?.forEach((row: ManShiftData) => {
    // group actions by user, if created_by or deleted_by is null, group together with key 0
    const createdById = row.created_by ?? 0;
    const deletedById = row.deleted_by ?? 0;

    if (row.created_at) {
      if (!manshiftUpdatesByUser[createdById]) {
        manshiftUpdatesByUser[createdById] = [];
      }

      manshiftUpdatesByUser[createdById].push(
        createTimelineEntry({
          operation: TimelineOperation.CREATE,
          time: row.created_at,
          data: {
            manShiftId: row.id,
            person: row.createdBy,
          },
        })
      );
    }

    if (row.deleted_at) {
      if (!manshiftUpdatesByUser[deletedById]) {
        manshiftUpdatesByUser[deletedById] = [];
      }

      manshiftUpdatesByUser[deletedById].push(
        createTimelineEntry({
          operation: TimelineOperation.DELETE,
          time: row.deleted_at,
          data: {
            manShiftId: row.id,
            person: row.deletedBy,
          },
        })
      );
    }
  });

  // create clusters of users and times of actions
  const clustersByUserAndTime: ManShiftRecord[][] = [];

  Object.entries(manshiftUpdatesByUser).forEach(([key, userUpdates]) => {
    // sort by time of the action in every user group
    const sortedUserUpdates = userUpdates.sort(
      (a, b) => moment(a.time).valueOf() - moment(b.time).valueOf()
    );

    sortedUserUpdates.forEach((update, i) => {
      if (i === 0) {
        clustersByUserAndTime.push([update]);
        return;
      }
      // put in one cluster if the time difference is smaller than 60 seconds
      const timeDifference = moment
        .duration(
          moment(update.time).diff(moment(sortedUserUpdates[i - 1].time))
        )
        .as('minutes');

      if (timeDifference <= 1) {
        clustersByUserAndTime[clustersByUserAndTime.length - 1].push(update);
      } else {
        clustersByUserAndTime.push([update]);
      }
    });
  });

  // sort clusters by time
  clustersByUserAndTime.sort(
    (a, b) => moment(a[0].time).valueOf() - moment(b[0].time).valueOf()
  );

  return clustersByUserAndTime;
};

export const manshiftCountInCluster = (cluster: ManShiftRecord[]) => {
  const count = cluster
    ?.map((row) => (row.operation === TimelineOperation.DELETE ? -1 : 1))
    .reduce((a, b) => a + b, 0);
  if (count > 0) return `+${count}`;
  return count;
};
